import isValidNpi from './npi';

const sourceLabels = {
  athena: 'athenaOne',
  nppes: 'NPPES',
};

const compact = values => values.filter(Boolean).join(' ');

export const getProviderDisplayName = provider => {
  if (!provider) {
    return '';
  }
  if (provider.displayName) {
    return provider.displayName;
  }
  const name = provider.name || provider;
  return compact([name.prefix, name.firstName, name.middleName, name.lastName, name.suffix, name.credential]);
};

const makeOption = (field, proposal, index, fieldValue, displayValue) => ({
  value: `${field}:${proposal.source}:${index}`,
  label: `${displayValue} (${sourceLabels[proposal.source] || proposal.source})`,
  source: proposal.source,
  fieldValue,
});

export const getProviderCompositionOptions = match => {
  const options = {
    name: [],
    npi: [],
    status: [],
    specialty: [],
    organization: [],
    directAddress: [],
    faxNumber: [],
  };

  (match?.proposals || []).forEach((proposal, proposalIndex) => {
    const name = getProviderDisplayName(proposal);
    if (name) {
      options.name.push(makeOption('name', proposal, proposalIndex, proposal.name, name));
    }
    if (proposal.npi) {
      options.npi.push(makeOption('npi', proposal, proposalIndex, proposal.npi, proposal.npi));
    }
    if (proposal.status) {
      options.status.push(makeOption('status', proposal, proposalIndex, proposal.status, proposal.status));
    }
    (proposal.specialties || []).forEach((specialty, index) => {
      const display = compact([specialty.name, specialty.code ? `(${specialty.code})` : '']);
      options.specialty.push(makeOption('specialty', proposal, `${proposalIndex}:${index}`, specialty, display));
    });
    (proposal.organizations || []).forEach((organization, index) => {
      options.organization.push(
        makeOption('organization', proposal, `${proposalIndex}:${index}`, organization.name, organization.name),
      );
    });
    (proposal.directAddresses || []).forEach((address, index) => {
      options.directAddress.push(
        makeOption('directAddress', proposal, `${proposalIndex}:${index}`, address.value, address.value),
      );
    });
    (proposal.faxNumbers || []).forEach((fax, index) => {
      options.faxNumber.push(makeOption('faxNumber', proposal, `${proposalIndex}:${index}`, fax.value, fax.value));
    });
  });

  return options;
};

const firstPreferredOption = options => options.find(option => option.source === 'athena') || options[0];

export const getDefaultProviderSelections = options =>
  Object.entries(options).reduce((selections, [field, values]) => {
    const preferred =
      field === 'directAddress' || field === 'faxNumber'
        ? values.find(option => option.source === 'nppes') || values[0]
        : firstPreferredOption(values);
    if (preferred) {
      return { ...selections, [field]: preferred.value };
    }
    return selections;
  }, {});

const selectedOption = (options, selections, field) =>
  options[field]?.find(option => option.value === selections[field]);

export const composeExternalProvider = (match, selections) => {
  const options = getProviderCompositionOptions(match);
  const name = selectedOption(options, selections, 'name');
  const npi = selectedOption(options, selections, 'npi');
  const status = selectedOption(options, selections, 'status');
  const specialty = selectedOption(options, selections, 'specialty');
  const organization = selectedOption(options, selections, 'organization');
  const directAddress = selectedOption(options, selections, 'directAddress');
  const faxNumber = selectedOption(options, selections, 'faxNumber');

  return {
    npi: npi?.fieldValue || '',
    firstName: name?.fieldValue?.firstName || '',
    middleName: name?.fieldValue?.middleName || '',
    lastName: name?.fieldValue?.lastName || '',
    status: status?.fieldValue || '',
    specialtyCode: specialty?.fieldValue?.code || '',
    specialtyName: specialty?.fieldValue?.name || '',
    organizationName: organization?.fieldValue || '',
    directAddress: directAddress?.fieldValue || '',
    faxNumber: faxNumber?.fieldValue || '',
    resolutionStatus: npi && (directAddress || faxNumber) ? 'resolved' : npi ? 'missing_channel' : 'missing_npi',
  };
};

export const composeManualExternalProvider = values => {
  const provider = {
    npi: values.npi?.trim() || '',
    firstName: values.firstName?.trim() || '',
    middleName: values.middleName?.trim() || '',
    lastName: values.lastName?.trim() || '',
    status: values.status?.trim() || '',
    specialtyCode: values.specialtyCode?.trim() || '',
    specialtyName: values.specialtyName?.trim() || '',
    organizationName: values.organizationName?.trim() || '',
    directAddress: values.directAddress?.trim() || '',
    faxNumber: values.faxNumber?.trim() || '',
  };
  return {
    ...provider,
    resolutionStatus:
      provider.npi && (provider.directAddress || provider.faxNumber)
        ? 'resolved'
        : provider.npi
        ? 'missing_channel'
        : 'missing_npi',
  };
};

export const isExternalProviderReady = provider =>
  Boolean(
    provider &&
      provider.firstName?.trim() &&
      provider.lastName?.trim() &&
      isValidNpi(provider.npi?.trim()) &&
      (provider.directAddress?.trim() || provider.faxNumber?.trim()),
  );

export const isAcceptedExternalProviderReady = (provider, isAccepted) =>
  Boolean(isAccepted && isExternalProviderReady(provider));

export const getExternalProviderSearchValidation = ({ searchMode, npi, firstName, lastName }) => {
  if (searchMode === 'npi') {
    const normalizedNpi = npi?.trim() || '';
    return {
      npi: !normalizedNpi ? 'required' : isValidNpi(normalizedNpi) ? undefined : 'invalid',
    };
  }

  return {
    firstName: firstName?.trim() ? undefined : 'required',
    lastName: lastName?.trim() ? undefined : 'required',
  };
};

export const externalProviderToDTO = provider => {
  if (!provider) {
    return undefined;
  }
  return {
    npi: provider.npi?.trim(),
    first_name: provider.firstName?.trim(),
    middle_name: provider.middleName?.trim() || undefined,
    last_name: provider.lastName?.trim(),
    status: provider.status?.trim() || undefined,
    specialty_code: provider.specialtyCode?.trim() || undefined,
    specialty_name: provider.specialtyName?.trim() || undefined,
    organization_name: provider.organizationName?.trim() || undefined,
    direct_address: provider.directAddress?.trim() || undefined,
    fax_number: provider.faxNumber?.trim() || undefined,
  };
};

export const externalProviderToFullDTO = provider => {
  if (!provider) {
    return null;
  }
  return {
    npi: provider.npi?.trim() || '',
    first_name: provider.firstName?.trim() || '',
    middle_name: provider.middleName?.trim() || '',
    last_name: provider.lastName?.trim() || '',
    status: provider.status?.trim() || '',
    specialty_code: provider.specialtyCode?.trim() || '',
    specialty_name: provider.specialtyName?.trim() || '',
    organization_name: provider.organizationName?.trim() || '',
    direct_address: provider.directAddress?.trim() || '',
    fax_number: provider.faxNumber?.trim() || '',
  };
};

export const getExternalProviderUpdateError = (response, successType, fallbackMessage) => {
  if (response?.type === successType) {
    return null;
  }
  return (
    response?.response?.data?.standard_error?.message ||
    response?.response?.data?.error?.message ||
    response?.response?.data?.error_msg ||
    response?.message ||
    response?.error?.message ||
    (typeof response?.error === 'string' ? response.error : null) ||
    fallbackMessage
  );
};
